import * as THREE from 'three';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import { AssetLoader } from '../loading/AssetLoader';
import { KENNEY_SCALE } from '../config';
import { npcCharacters } from '../loading/AssetManifest';
import type { SidewalkTile } from './PickupSites';

/**
 * A waving pedestrian placed on the curb next to an active marker.
 *
 * Kenney mini-characters are rigged SkinnedMesh hierarchies, so
 * `gltf.scene.clone()` would leave the clone pointing at the original
 * skeleton — the visible result is either nothing or a T-pose at the
 * world origin. `SkeletonUtils.clone` duplicates bones properly.
 *
 * The GLBs don't ship a wave animation clip, so we drive a single arm
 * bone directly: find the right (or left) arm, raise it overhead, then
 * oscillate around the raised pose for the waving motion. The body
 * itself stands still — no more "jumping NPC".
 */
const ARM_RAISE_RAD = Math.PI * 0.55;   // how high the arm is lifted
const WAVE_AMPLITUDE_RAD = 0.35;        // size of the side-to-side wave
const WAVE_HZ = 2.2;                    // waves per second

export class NpcPassenger {
  private group: THREE.Group;
  private current: THREE.Group | null = null;
  private loader: AssetLoader;
  private time = 0;
  private baseYaw = 0;
  private lastCharacter = '';
  private waveBone: THREE.Bone | null = null;
  private waveBoneBaseZ = 0;

  constructor(scene: THREE.Scene, loader: AssetLoader) {
    this.loader = loader;
    this.group = new THREE.Group();
    this.group.visible = false;
    scene.add(this.group);
  }

  private static loggedBonesFor = new Set<string>();

  /** Scan a SkinnedMesh hierarchy for a bone that looks like an arm.
   * Kenney names vary — priority is right-side, then left-side, then any
   * bone with "arm" / "shoulder" in the name. */
  private static findArmBone(root: THREE.Object3D): THREE.Bone | null {
    const bones: THREE.Bone[] = [];
    root.traverse((o) => {
      if (o instanceof THREE.Bone) bones.push(o);
    });
    const match = (patterns: RegExp[]) => {
      for (const b of bones) {
        if (patterns.some((p) => p.test(b.name))) return b;
      }
      return null;
    };
    return (
      match([/arm[._-]?r$/i, /r[._-]?arm/i, /rightarm/i, /shoulder[._-]?r/i]) ??
      match([/arm[._-]?l$/i, /l[._-]?arm/i, /leftarm/i, /shoulder[._-]?l/i]) ??
      match([/arm|shoulder/i])
    );
  }

  show(tile: SidewalkTile) {
    this.hide();

    // Pick a character at random, avoiding a repeat of the previous spawn
    // so consecutive fares always look different.
    let name = npcCharacters[Math.floor(Math.random() * npcCharacters.length)];
    if (npcCharacters.length > 1 && name === this.lastCharacter) {
      name = npcCharacters[(npcCharacters.indexOf(name) + 1) % npcCharacters.length];
    }
    this.lastCharacter = name;

    const key = `npc/${name}`;
    const gltf = this.loader.getOriginal(key);
    if (!gltf) {
      // Phase 3 assets haven't streamed yet — silent no-op. The main.ts
      // post-phase-3 hook re-spawns the current pickup once they land.
      console.warn(`[NpcPassenger] asset not loaded yet: ${key}`);
      return;
    }

    const model = cloneSkeleton(gltf.scene) as THREE.Group;
    // KENNEY_SCALE * 0.3 leaves the mini-characters roughly at player height.
    // Bumped from 0.25 because the Kenney models are smaller native-unit-wise
    // than the cars that use the same factor.
    model.scale.setScalar(KENNEY_SCALE * 0.3);
    model.traverse((c) => {
      if (c instanceof THREE.Mesh || c instanceof THREE.SkinnedMesh) {
        c.castShadow = true;
        c.receiveShadow = true;
        c.frustumCulled = false;
      }
    });

    this.group.add(model);
    this.current = model;

    // Stand at the curb, not the tile centre — the tile centre sits inside
    // the adjacent building mesh and hides the NPC from view. The curb is
    // on the sidewalk edge, in the same spot as the beam marker.
    this.group.position.set(tile.curbX, 0, tile.curbZ);
    // facingX/Z points from tile centre toward the road — that's the direction
    // the NPC should look to watch for the arriving taxi.
    this.baseYaw = Math.atan2(tile.facingX, tile.facingZ);
    this.group.rotation.y = this.baseYaw;
    this.group.visible = true;
    this.time = 0;

    // Locate an arm bone once and pre-raise it into the waving pose. Log
    // bone names the first time we see a given model so we can iterate on
    // the name patterns if findArmBone guesses wrong.
    this.waveBone = NpcPassenger.findArmBone(model);
    this.waveBoneBaseZ = this.waveBone ? this.waveBone.rotation.z : 0;
    if (!NpcPassenger.loggedBonesFor.has(name)) {
      NpcPassenger.loggedBonesFor.add(name);
      const allBones: string[] = [];
      model.traverse((o) => {
        if (o instanceof THREE.Bone) allBones.push(o.name);
      });
      console.log(
        `[NpcPassenger] ${name} bones:`,
        allBones,
        '→ wave bone:',
        this.waveBone?.name ?? 'none found',
      );
    }
  }

  hide() {
    if (this.current) {
      this.group.remove(this.current);
      this.current = null;
    }
    this.waveBone = null;
    this.group.visible = false;
  }

  update(delta: number) {
    if (!this.current) return;
    this.time += delta;

    // Body stays planted — no bouncing, no yaw sway. The NPC stands
    // normally; only the arm moves.
    if (this.waveBone) {
      // Raise the arm overhead (constant offset) and oscillate around the
      // raised pose for the wave itself.
      const wave = Math.sin(this.time * Math.PI * 2 * WAVE_HZ) * WAVE_AMPLITUDE_RAD;
      this.waveBone.rotation.z = this.waveBoneBaseZ - ARM_RAISE_RAD + wave;
    }
  }
}
